import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Landmark, TriangleAlert } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useLinkedSponsorNames } from '@/components/political/CampaignTransparency'
import { ELECTIONS_QUERY_KEY, isRecent } from '@/components/political/ElectionSelect'
import type { Advertiser } from '@/services/advertisers/advertisersApi'
import { ElectionsApi } from '@/services/political/electionsApi'
import type { PoliticalCampaign } from '@/types/models'
import { formatDate } from '@/utils/format'

interface PoliticalLinkStatusProps {
  /** The advertiser the order or line item runs for. */
  advertiser?: Advertiser
  /** The object itself is political, e.g. a line item that names an election. */
  political?: boolean
  /**
   * The advertiser's linked campaigns. Those whose election is still current
   * are listed, since an advertiser can be linked to several at once.
   */
  campaigns?: PoliticalCampaign[]
  /** The election the line item is booked against, marked in the list. */
  electionId?: string
}

/**
 * Says up front whether a political order or line item is backed by a linked
 * political organisation: who it is when linked, and a loud warning when the
 * advertiser is political, or the object is, but nothing is linked yet.
 * Renders nothing for ordinary advertising, or for an advertiser that declares
 * its own political budgets instead of linking.
 */
export function PoliticalLinkStatus({ advertiser, political, campaigns, electionId }: PoliticalLinkStatusProps) {
  const { t } = useTranslation()
  const sponsors = useLinkedSponsorNames(advertiser?.politicalCampaigns ?? [])
  const { data: elections = [] } = useQuery({
    queryKey: ELECTIONS_QUERY_KEY,
    queryFn: ElectionsApi.getAll,
    staleTime: 5 * 60 * 1000,
    enabled: !!campaigns?.length,
  })
  if (!advertiser) return null

  const electionsById = new Map(elections.map((e) => [e.id, e]))
  // An election missing from the list is kept: better listed than hidden.
  const activeCampaigns = (campaigns ?? []).filter((c) => {
    const election = electionsById.get(c.electionId)
    return !election || isRecent(election)
  })

  const linked = advertiser.politicalCampaignIds.length > 0
  const isPolitical = !!advertiser.advertiserType || !!political
  if (!linked && !isPolitical) return null
  // An advertiser declaring its own political budgets runs the older
  // workflow, where everything political lives on the advertiser — there is
  // no organisation to link, so nothing is missing.
  if (!linked && advertiser.politicalBudgets.length > 0) return null

  const advertiserLink = (
    // Dressed in the alert's own colour so it reads as part of the message.
    <Button
      asChild
      variant="outline"
      size="xs"
      className="mt-2 flex w-fit border-current/30 bg-transparent text-sm text-current! no-underline shadow-none hover:bg-current/10 hover:text-current!dark:border-current/30 dark:bg-transparent dark:hover:bg-current/10"
    >
      <Link to={`/advertising/advertisers/${advertiser.id}`}>{t('political.linkStatus.openAdvertiser')}</Link>
    </Button>
  )

  if (!linked) {
    return (
      <Alert variant="destructive">
        <TriangleAlert />
        <AlertTitle>{t('political.linkStatus.missingTitle')}</AlertTitle>
        <AlertDescription>
          {t('political.linkStatus.missingBody', { advertiser: advertiser.name })}
          {advertiserLink}
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <Alert variant="info">
      <Landmark />
      <AlertTitle>
        {sponsors
          ? t('advertisers.politicalLinkedTo', { name: sponsors })
          : t('advertisers.politicalLinked')}
      </AlertTitle>
      <AlertDescription>
        {t('political.linkStatus.linkedBody', { advertiser: advertiser.name })}
        {activeCampaigns.length > 0 && (
          <div className="mt-2 flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-foreground">
              {t('political.linkStatus.activeCampaigns', { count: activeCampaigns.length })}
            </span>
            <ul className="flex flex-col gap-1">
              {activeCampaigns.map((c) => {
                const election = electionsById.get(c.electionId)
                const details = [
                  election?.name || c.electionName,
                  election?.electionDate && formatDate(election.electionDate),
                  c.politicalEntityName,
                ].filter(Boolean)
                return (
                  <li key={c.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="font-medium text-foreground">{c.name}</span>
                    {details.length > 0 && <span>{details.join(' · ')}</span>}
                    {!!electionId && c.electionId === electionId && (
                      <Badge variant="blue">{t('political.linkStatus.bookedAgainst')}</Badge>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </AlertDescription>
    </Alert>
  )
}
